import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FiRefreshCw, FiXCircle, FiSearch, FiChevronLeft, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { adminApi } from '../../api';
import { Loader } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { showToast } from '../../utils/toast';
const PAGE_SIZE = 10;

type ViewMode = 'allOanda' | 'masterFollowers';
type BrokerFilter = 'none' | 'all' | 'oanda' | 'webull' | 'genesis';

type PositionItem = {
  connectionId: string;
  userId: string;
  accountId?: string;
  symbol: string;
  side: string;
  qty: number;
  userName: string;
  userEmail?: string;
  avgEntryPrice?: number;
  unrealizedPl?: number;
  unrealizedPlPercent?: number;
  marketValue?: number;
  connectionType?: string;
  brokerageName?: string;
  productType?: string;
  genesisPositionId?: string;
};

type PositionDirection = 'long' | 'short';

const getPositionDirection = (position: Pick<PositionItem, 'side' | 'qty'>): PositionDirection =>
  position.side === 'long' || position.qty > 0 ? 'long' : 'short';

const buildFollowerClosePayload = (position: PositionItem, selectedMasterId?: string, qtyOverride?: number) => {
  const direction = getPositionDirection(position);
  const rawT = String(position.connectionType ?? 'oanda').toLowerCase();
  const qty = qtyOverride != null && Number.isFinite(qtyOverride) && qtyOverride > 0 ? qtyOverride : Math.abs(position.qty);
  if (rawT === 'genesis') {
    return {
      connectionId: position.connectionId,
      instrument: position.symbol,
      connectionType: 'genesis' as const,
      positionId: position.genesisPositionId,
      qty,
      side: direction,
      productType: position.productType,
      masterId: selectedMasterId,
    };
  }
  const connectionType: 'oanda' | 'snaptrade' = rawT === 'snaptrade' ? 'snaptrade' : 'oanda';
  return {
    connectionId: position.connectionId,
    instrument: position.symbol,
    connectionType,
    qty,
    side: direction,
    productType: position.productType,
    masterId: selectedMasterId,
    ...(connectionType === 'oanda'
      ? (direction === 'long' ? { longUnits: 'ALL' as const } : { shortUnits: 'ALL' as const })
      : {}),
  };
};

const getBrokerLabel = (position: PositionItem) =>
  position.brokerageName ||
  (position.connectionType === 'snaptrade'
    ? 'SnapTrade'
    : position.connectionType === 'oanda'
      ? 'OANDA'
      : position.connectionType === 'genesis'
        ? 'GenesisFX'
        : '—');

const AdminOpenPositions: React.FC = () => {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ViewMode>('allOanda');
  const [brokerFilter, setBrokerFilter] = useState<BrokerFilter>('none');
  const [masterId, setMasterId] = useState<string>('');
  const [allModeMasterId, setAllModeMasterId] = useState<string>('');
  const [allModeMastersLoaded, setAllModeMastersLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  /** Sent to API; only updates when user clicks Search or presses Enter. */
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [closeQuantity, setCloseQuantity] = useState<string>('');
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
  const [positionToCloseSingle, setPositionToCloseSingle] = useState<PositionItem | null>(null);
  const [isBulkClosing, setIsBulkClosing] = useState(false);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [locallyClosedIds, setLocallyClosedIds] = useState<Set<string>>(new Set());
  const [showCloseAllModal, setShowCloseAllModal] = useState(false);
  const [closeAllConfirmText, setCloseAllConfirmText] = useState('');
  const [isClosingAll, setIsClosingAll] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const forceRefreshOpenPositionsRef = useRef(false);
  const didInitialBrokerPullAllRef = useRef(false);
  const didInitialBrokerPullMasterFollowersRef = useRef(false);
  const prevViewModeRef = useRef<ViewMode | null>(null);

  useEffect(() => {
    const prev = prevViewModeRef.current;
    if (viewMode === 'allOanda' && prev !== null && prev !== 'allOanda') {
      didInitialBrokerPullAllRef.current = false;
    }
    if (viewMode === 'masterFollowers' && prev !== null && prev !== 'masterFollowers') {
      didInitialBrokerPullMasterFollowersRef.current = false;
    }
    prevViewModeRef.current = viewMode;
  }, [viewMode]);

  useEffect(() => {
    didInitialBrokerPullMasterFollowersRef.current = false;
  }, [masterId]);

  const applyUserSearch = () => {
    setAppliedSearch(searchQuery.trim());
    setPage(1);
  };

  const searchDisabled =
    (viewMode === 'masterFollowers' && !masterId) || (viewMode === 'allOanda' && brokerFilter === 'none');

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
    setLocallyClosedIds(new Set());
  }, [viewMode, masterId, brokerFilter]);

  useEffect(() => {
    setAllModeMasterId('');
    setAllModeMastersLoaded(false);
  }, [brokerFilter]);

  const {
    data: allOandaData,
    isLoading: loadingAll,
    error: errorAll,
    refetch: refetchAll,
  } = useQuery({
    queryKey: ['admin', 'open-positions', page, appliedSearch, brokerFilter],
    queryFn: () => {
      const forceLive = forceRefreshOpenPositionsRef.current;
      forceRefreshOpenPositionsRef.current = false;
      return adminApi.getOandaOpenPositions({
        page,
        limit: PAGE_SIZE,
        search: appliedSearch || undefined,
        forceRefresh: forceLive,
        brokerFilter: brokerFilter === 'none' ? undefined : brokerFilter,
      });
    },
    // Master-scoped list uses its own query — avoid two parallel open-position fetches.
    enabled: viewMode === 'allOanda' && brokerFilter !== 'none' && !allModeMasterId,
    refetchOnWindowFocus: false,
  });

  const {
    data: allModeMasterData,
    isLoading: loadingAllByMaster,
    error: errorAllByMaster,
    refetch: refetchAllByMaster,
  } = useQuery({
    queryKey: ['admin', 'open-positions-master-filter', allModeMasterId, page, appliedSearch, brokerFilter],
    queryFn: () => {
      const forceLive = forceRefreshOpenPositionsRef.current;
      forceRefreshOpenPositionsRef.current = false;
      return adminApi.getMasterFollowersOpenPositions({
        masterId: allModeMasterId,
        page,
        limit: PAGE_SIZE,
        search: appliedSearch || undefined,
        forceRefresh: forceLive,
        brokerFilter: brokerFilter === 'none' ? undefined : brokerFilter,
      });
    },
    enabled: viewMode === 'allOanda' && brokerFilter !== 'none' && !!allModeMasterId,
    refetchOnWindowFocus: false,
  });

  const { data: mastersData } = useQuery({
    queryKey: ['admin', 'masters-list-open-pos', brokerFilter],
    queryFn: () =>
      adminApi.getMasters({
        brokerFilter: brokerFilter === 'none' ? 'all' : brokerFilter,
      }),
    enabled:
      (viewMode === 'masterFollowers' && brokerFilter !== 'none') ||
      (viewMode === 'allOanda' && brokerFilter !== 'none' && allModeMastersLoaded),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const {
    data: mfData,
    isLoading: loadingMf,
    error: errorMf,
    refetch: refetchMf,
  } = useQuery({
    queryKey: ['admin', 'master-followers-open-positions', masterId, page, appliedSearch, brokerFilter],
    queryFn: () => {
      if (!didInitialBrokerPullMasterFollowersRef.current) {
        didInitialBrokerPullMasterFollowersRef.current = true;
      }
      const forceLive = forceRefreshOpenPositionsRef.current;
      forceRefreshOpenPositionsRef.current = false;
      return adminApi.getMasterFollowersOpenPositions({
        masterId: masterId!,
        page,
        limit: PAGE_SIZE,
        search: appliedSearch || undefined,
        forceRefresh: forceLive,
        brokerFilter: brokerFilter === 'none' ? undefined : brokerFilter,
      });
    },
    enabled: viewMode === 'masterFollowers' && !!masterId,
    refetchOnWindowFocus: false,
  });

  const normConnType = (p: PositionItem) => String(p.connectionType ?? 'oanda').toLowerCase();

  const rowId = (p: PositionItem) =>
    `${p.userId || 'unknown'}-${p.connectionId}-${normConnType(p)}-${p.symbol}-${p.side}`;

  const isCloseable = (p: PositionItem) => {
    const type = normConnType(p);
    if (type === 'genesis') {
      const positionId = String(p.genesisPositionId || '').trim();
      return positionId.length > 0 && positionId !== 'undefined';
    }
    return type === 'oanda' || type === 'snaptrade';
  };
  /** Rows that support admin bulk selection (same set as single-row Close). */
  const isBulkSelectable = (p: PositionItem) => isCloseable(p);

  const rawPositions: PositionItem[] =
    viewMode === 'allOanda'
      ? allModeMasterId
        ? (allModeMasterData?.positions ?? [])
        : (allOandaData?.positions ?? [])
      : (mfData?.positions ?? []);
  const positions = useMemo(() => {
    const visible = rawPositions.filter((p) => !locallyClosedIds.has(rowId(p)));
    if (!visible.length) return visible;

    const byUser = new Map<string, PositionItem[]>();
    visible.forEach((p) => {
      const uid = String(p.userId || '');
      if (!uid) return;
      if (!byUser.has(uid)) byUser.set(uid, []);
      byUser.get(uid)!.push(p);
    });

    const ordered: PositionItem[] = [];
    const pushUser = (userId?: string) => {
      if (!userId) return;
      const bucket = byUser.get(String(userId));
      if (!bucket || bucket.length === 0) return;
      ordered.push(...bucket);
      byUser.delete(String(userId));
    };

    if (viewMode === 'allOanda' && !allModeMasterId) {
      const seq = allOandaData?.masterSequence ?? [];
      seq.forEach((s: any) => {
        pushUser(s.masterId);
        (s.followerIds || []).forEach((fid: string) => pushUser(fid));
      });
    } else if (viewMode === 'allOanda' && allModeMasterId) {
      pushUser(allModeMasterId);
      (allModeMasterData?.followers || []).forEach((f: any) => pushUser(f.followerId));
    } else if (viewMode === 'masterFollowers' && masterId) {
      pushUser(masterId);
      (mfData?.followers || []).forEach((f: any) => pushUser(f.followerId));
    }

    const leftovers = Array.from(byUser.values())
      .flat()
      .sort((a, b) => (a.userName || '').localeCompare(b.userName || ''));
    return [...ordered, ...leftovers];
  }, [
    rawPositions,
    locallyClosedIds,
    viewMode,
    allModeMasterId,
    allOandaData,
    allModeMasterData,
    mfData,
    masterId
  ]);
  const pagination =
    viewMode === 'allOanda'
      ? allModeMasterId
        ? allModeMasterData?.pagination ?? { page: 1, limit: PAGE_SIZE, total: 0, pages: 1 }
        : allOandaData?.pagination ?? { page: 1, limit: PAGE_SIZE, total: 0, pages: 1 }
      : mfData?.pagination ?? { page: 1, limit: PAGE_SIZE, total: 0, pages: 1 };

  const isLoading =
    viewMode === 'allOanda'
      ? allModeMasterId
        ? loadingAllByMaster
        : loadingAll
      : !masterId
        ? false
        : loadingMf;
  const error = viewMode === 'allOanda' ? (allModeMasterId ? errorAllByMaster : errorAll) : errorMf;

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectable = positions.filter(isBulkSelectable);
    const allOnPageSelected =
      selectable.length > 0 && selectable.every((p) => selectedIds.has(rowId(p)));
    if (allOnPageSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectable.map((p) => rowId(p))));
    }
  };

  const invalidatePositionQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'open-positions'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'open-positions-master-filter'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'master-followers-open-positions'] });
  };

  const closeOne = async (position: PositionItem) => {
    if (!isCloseable(position)) {
      showToast('error', 'Close from here is not supported for this broker connection.');
      return;
    }
    const id = rowId(position);
    const startedAt = Date.now();
    setClosingId(id);
    try {
      await adminApi.closeFollowerPositionAdmin({
        ...buildFollowerClosePayload(
          position,
          viewMode === 'masterFollowers' && masterId ? masterId : undefined
        ),
      });
      setLocallyClosedIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setPositionToCloseSingle(null);
      showToast('success', `Position ${position.symbol} closed for ${position.userName}`);
      invalidatePositionQueries();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to close position');
    } finally {
      const elapsed = Date.now() - startedAt;
      const minVisibleMs = 700;
      if (elapsed < minVisibleMs) {
        await new Promise((resolve) => setTimeout(resolve, minVisibleMs - elapsed));
      }
      setClosingId(null);
    }
  };

  const confirmSingleClose = () => {
    if (positionToCloseSingle) closeOne(positionToCloseSingle);
  };

  const handleBulkClose = async () => {
    if (selectedIds.size === 0) return;
    const toClose = positions.filter((p) => selectedIds.has(rowId(p)) && isBulkSelectable(p));
    if (toClose.length === 0) return;
    const startedAt = Date.now();
    setIsBulkClosing(true);
    const qtyNum = closeQuantity.trim() === '' ? null : parseInt(closeQuantity, 10);
    const isValidQty = qtyNum != null && !Number.isNaN(qtyNum) && qtyNum > 0;
    const masterIdForClose = viewMode === 'masterFollowers' && masterId ? masterId : undefined;

    const oandaRows = toClose.filter((p) => normConnType(p) === 'oanda');
    const snapRows = toClose.filter((p) => normConnType(p) === 'snaptrade');
    const genesisRows = toClose.filter((p) => normConnType(p) === 'genesis');

    const oandaResults = await Promise.allSettled(
      oandaRows.map((p) => {
        const isLong = p.side === 'long' || p.qty > 0;
        const posQty = Math.abs(p.qty);
        const units = isValidQty ? Math.min(qtyNum!, posQty) : 'ALL';
        return adminApi.closeOandaPosition({
          connectionId: p.connectionId,
          instrument: p.symbol,
          ...(isLong ? { longUnits: units } : { shortUnits: units }),
        });
      })
    );

    const snapResults = await Promise.allSettled(
      snapRows.map((p) => {
        const posQty = Math.abs(p.qty);
        const snapQty = isValidQty ? Math.min(qtyNum!, posQty) : posQty;
        return adminApi.closeFollowerPositionAdmin(
          buildFollowerClosePayload(p, masterIdForClose, snapQty)
        );
      })
    );

    const genesisResults = await Promise.allSettled(
      genesisRows.map((p) => {
        const posQty = Math.abs(p.qty);
        const genesisQty = isValidQty ? Math.min(qtyNum!, posQty) : posQty;
        return adminApi.closeFollowerPositionAdmin(
          buildFollowerClosePayload(p, masterIdForClose, genesisQty)
        );
      })
    );

    const results = [...oandaResults, ...snapResults, ...genesisResults];
    const closedIds = new Set<string>();
    oandaResults.forEach((r, idx) => {
      if (r.status === 'fulfilled') closedIds.add(rowId(oandaRows[idx]));
    });
    snapResults.forEach((r, idx) => {
      if (r.status === 'fulfilled') closedIds.add(rowId(snapRows[idx]));
    });
    genesisResults.forEach((r, idx) => {
      if (r.status === 'fulfilled') closedIds.add(rowId(genesisRows[idx]));
    });
    if (closedIds.size > 0) {
      setLocallyClosedIds((prev) => {
        const next = new Set(prev);
        closedIds.forEach((id) => next.add(id));
        return next;
      });
    }
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (succeeded > 0) {
      showToast('success', `Closed ${succeeded} position${succeeded > 1 ? 's' : ''}`);
    }
    if (failed > 0) {
      showToast('error', `Failed to close ${failed} position${failed > 1 ? 's' : ''}`);
    }
    setSelectedIds(new Set());
    setCloseQuantity('');
    invalidatePositionQueries();
    const elapsed = Date.now() - startedAt;
    const minVisibleMs = 700;
    if (elapsed < minVisibleMs) {
      await new Promise((resolve) => setTimeout(resolve, minVisibleMs - elapsed));
    }
    setIsBulkClosing(false);
    setShowCloseConfirmModal(false);
  };

  const openBulkCloseConfirm = () => {
    if (selectedIds.size === 0) return;
    setShowCloseConfirmModal(true);
  };

  const handleCloseAllFollowersAdmin = async () => {
    if (!masterId) return;
    if (closeAllConfirmText.trim() !== 'CLOSE ALL') {
      showToast('error', 'Type CLOSE ALL to confirm.');
      return;
    }
    setIsClosingAll(true);
    try {
      const res = await adminApi.closeAllFollowersPositionsAdmin({ masterId });
      const { succeeded, failed } = res.results || { succeeded: 0, failed: 0 };
      if (succeeded > 0) showToast('success', `Submitted close for ${succeeded} position(s).`);
      if (failed > 0) showToast('error', `${failed} position(s) could not be closed.`);
      setShowCloseAllModal(false);
      setCloseAllConfirmText('');
      setLocallyClosedIds(new Set());
      invalidatePositionQueries();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Close-all failed');
    } finally {
      setIsClosingAll(false);
    }
  };

  const handleRefresh = async () => {
    if (viewMode === 'allOanda' && brokerFilter === 'none') {
      showToast('info', 'Please select a broker first');
      return;
    }
    setIsManualRefreshing(true);
    try {
      if (viewMode === 'allOanda') {
        forceRefreshOpenPositionsRef.current = true;
        if (allModeMasterId) await refetchAllByMaster();
        else await refetchAll();
      } else if (viewMode === 'masterFollowers' && masterId) {
        forceRefreshOpenPositionsRef.current = true;
        await refetchMf();
      }
    } finally {
      setIsManualRefreshing(false);
    }
  };

  if (isLoading && rawPositions.length === 0) {
    return <Loader message="Loading open positions..." />;
  }

  if (error && viewMode === 'allOanda') {
    return <ErrorDisplay error="Failed to load open positions" />;
  }

  if (error && viewMode === 'masterFollowers' && masterId) {
    return <ErrorDisplay error="Failed to load followers' positions for this master" />;
  }

  const masters = mastersData?.users ?? [];
  const selectedMasterLabel = masters.find((m) => m._id === masterId)?.name || '';
  const showBrokerCols = viewMode === 'masterFollowers';

  const selectableOnPage = positions.filter(isBulkSelectable);
  const allPageRowsSelected =
    selectableOnPage.length > 0 && selectableOnPage.every((p) => selectedIds.has(rowId(p)));

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-2xl lg:text-3xl font-bold text-foreground">Open Positions</h2>
          <p className="text-muted mt-1 text-sm lg:text-base max-w-3xl leading-relaxed">
            {viewMode === 'allOanda'
              ? 'All open positions (OANDA, SnapTrade/Webull, and GenesisFX) are fetched live from broker connections and show only currently open positions.'
              : 'Pick a master to fetch followers’ live open positions from brokers (OANDA, SnapTrade/Webull, and GenesisFX).'}
          </p>
        </div>
        <div className="mt-2 lg:mt-3 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center rounded-lg border border-border bg-surface p-1 shadow-sm">
            <div className="relative">
              <select
                value={brokerFilter}
                onChange={(e) => {
                  const v = e.target.value as BrokerFilter;
                  if (v !== 'none') forceRefreshOpenPositionsRef.current = true;
                  setBrokerFilter(v);
                }}
                className={`min-w-[140px] sm:min-w-[165px] rounded-md cursor-pointer px-3 pr-8 py-2 text-sm font-semibold border border-border/70 appearance-none outline-none transition-colors ${
                  brokerFilter === 'none'
                    ? 'bg-slate-900 text-slate-100'
                    : 'bg-primary text-primary-foreground'
                }`}
                title="Select broker"
              >
                <option value="none" className="text-foreground bg-surface">Select Broker</option>
                <option value="all" className="text-foreground bg-surface">All brokers</option>
                <option value="oanda" className="text-foreground bg-surface">OANDA</option>
                <option value="webull" className="text-foreground bg-surface">Webull</option>
                <option value="genesis" className="text-foreground bg-surface">GenesisFX</option>
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-200" />
            </div>
            <button
              type="button"
              onClick={() => handleRefresh()}
              className="ml-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-foreground hover:bg-surface-elevated transition-colors"
            >
              <FiRefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
          {viewMode === 'masterFollowers' && masterId && pagination.total > 0 && (
            <button
              type="button"
              onClick={() => setShowCloseAllModal(true)}
              disabled={isClosingAll}
              className="flex items-center gap-2 px-4 py-2 bg-danger/15 border border-danger/40 text-danger font-semibold rounded-lg hover:bg-danger/25 transition-colors disabled:opacity-50"
            >
              <FiXCircle className="h-4 w-4" />
              Close all followers’ trades
            </button>
          )}
        </div>
      </div>

      {isManualRefreshing && (
        <div className="card py-3">
          <div className="flex items-center gap-2 text-sm text-muted">
            <FiRefreshCw className="h-4 w-4 animate-spin" />
            <span>Loading positions...</span>
          </div>
        </div>
      )}

      {viewMode === 'allOanda' && brokerFilter !== 'none' && (
        <div className="card">
          <label className="block text-sm font-medium text-foreground mb-2">Master trader</label>
          <select
            value={allModeMasterId}
            onChange={(e) => {
              forceRefreshOpenPositionsRef.current = true;
              setAllModeMasterId(e.target.value);
            }}
            onFocus={() => setAllModeMastersLoaded(true)}
            onClick={() => setAllModeMastersLoaded(true)}
            className="w-full max-w-md px-4 py-2.5 bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All masters</option>
            {masters.map((m) => (
              <option key={m._id} value={m._id}>
                {m.name} ({m.email})
              </option>
            ))}
          </select>
          {!allModeMastersLoaded && (
            <p className="mt-2 text-sm text-muted">
              Open this dropdown to load broker masters.
            </p>
          )}
          {allModeMastersLoaded && masters.length === 0 && (
            <p className="mt-2 text-sm text-muted">
              No masters found for selected broker.
            </p>
          )}
        </div>
      )}

      {viewMode === 'masterFollowers' && (
        <div className="card">
          <label className="block text-sm font-medium text-foreground mb-2">Master trader</label>
          <select
            value={masterId}
            onChange={(e) => {
              const v = e.target.value;
              if (v) forceRefreshOpenPositionsRef.current = true;
              setMasterId(v);
            }}
            className="w-full max-w-md px-4 py-2.5 bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select a master…</option>
            {masters.map((m) => (
              <option key={m._id} value={m._id}>
                {m.name} ({m.email})
              </option>
            ))}
          </select>
          {masterId && mfData?.master && (
            <p className="mt-2 text-sm text-muted">
              Showing positions for followers of <span className="text-foreground font-medium">{selectedMasterLabel}</span>
            </p>
          )}
        </div>
      )}

      <div className="card">
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <div className="relative flex-1 min-w-0">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted pointer-events-none" />
            <input
              type="text"
              placeholder={
                showBrokerCols
                  ? 'Search follower, email, symbol, or broker…'
                  : 'Search by user name, email, or symbol…'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (!searchDisabled) applyUserSearch();
              }}
              className="w-full pl-10 pr-4 py-2.5 bg-surface border border-border rounded-lg text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              disabled={searchDisabled}
            />
          </div>
          <button
            type="button"
            onClick={applyUserSearch}
            disabled={searchDisabled}
            className="btn-primary shrink-0 px-4 py-2.5 flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50"
          >
            <FiSearch className="h-4 w-4" />
            Search
          </button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="card bg-danger/5 border border-danger/20 flex flex-wrap items-center justify-between gap-3 sm:gap-4">
          <span className="text-sm font-semibold text-foreground min-w-0">
            {selectedIds.size} position{selectedIds.size > 1 ? 's' : ''} selected (OANDA, Webull/SnapTrade, GenesisFX)
          </span>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-foreground font-medium">Max quantity per row (optional):</label>
            <input
              type="number"
              min={1}
              placeholder="All"
              value={closeQuantity}
              onChange={(e) => setCloseQuantity(e.target.value.replace(/[^0-9]/g, ''))}
              className="w-28 px-3 py-2 bg-surface border border-border rounded-lg text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <span className="text-xs text-muted">
              (empty = full size caps OANDA units and SnapTrade/Webull qty per row)
            </span>
            <button
              onClick={openBulkCloseConfirm}
              disabled={isBulkClosing}
              className="flex items-center gap-2 px-4 py-2 bg-danger hover:opacity-90 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              <FiXCircle className="h-4 w-4" />
              {isBulkClosing ? 'Closing...' : 'Close selected'}
            </button>
          </div>
        </div>
      )}

      {showCloseConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 animate-fade-in"
          onClick={() => !isBulkClosing && setShowCloseConfirmModal(false)}>
          <div className="card max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-foreground mb-2">Confirm close selected positions</h3>
            <p className="text-sm text-muted mb-4">
              {closeQuantity.trim() && !Number.isNaN(parseInt(closeQuantity, 10)) && parseInt(closeQuantity, 10) > 0
                ? `Are you sure you want to close ${parseInt(closeQuantity, 10).toLocaleString()} units from each of ${selectedIds.size} selected position(s)?`
                : `Are you sure you want to close all units from each of ${selectedIds.size} selected position(s)?`}
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCloseConfirmModal(false)}
                disabled={isBulkClosing}
                className="px-4 py-2 bg-surface border border-border rounded-lg text-foreground hover:bg-surface-elevated transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkClose}
                disabled={isBulkClosing}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-danger hover:opacity-90 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {isBulkClosing ? (
                  <>
                    <FiRefreshCw className="h-4 w-4 animate-spin" />
                    <span>Closing...</span>
                  </>
                ) : (
                  'Confirm close'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCloseAllModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 animate-fade-in"
          onClick={() => !isClosingAll && setShowCloseAllModal(false)}
        >
          <div className="card max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-foreground mb-2">Close all followers’ trades</h3>
            <p className="text-sm text-muted mb-3">
              This will attempt to close <strong className="text-foreground">every open position</strong> stored for this
              master’s followers (OANDA, SnapTrade, Webull, etc.). Type <strong className="text-foreground">CLOSE ALL</strong>{' '}
              to confirm.
            </p>
            <input
              type="text"
              value={closeAllConfirmText}
              onChange={(e) => setCloseAllConfirmText(e.target.value)}
              placeholder="CLOSE ALL"
              className="w-full px-3 py-2 mb-4 bg-surface border border-border rounded-lg text-foreground"
              disabled={isClosingAll}
            />
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCloseAllModal(false);
                  setCloseAllConfirmText('');
                }}
                disabled={isClosingAll}
                className="px-4 py-2 bg-surface border border-border rounded-lg text-foreground hover:bg-surface-elevated disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleCloseAllFollowersAdmin()}
                disabled={isClosingAll}
                className="px-4 py-2 bg-danger hover:opacity-90 text-white font-semibold rounded-lg disabled:opacity-50"
              >
                {isClosingAll ? 'Closing…' : 'Confirm close all'}
              </button>
            </div>
          </div>
        </div>
      )}

      {positionToCloseSingle && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 animate-fade-in"
          onClick={() => !closingId && setPositionToCloseSingle(null)}
        >
          <div className="card max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-foreground mb-2">Confirm close position</h3>
            <p className="text-sm text-muted mb-4">
              Are you sure you want to close this position: <strong className="text-foreground">{positionToCloseSingle.symbol}</strong> (
              {Math.abs(positionToCloseSingle.qty).toLocaleString()} units) for <strong className="text-foreground">{positionToCloseSingle.userName}</strong>?
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => setPositionToCloseSingle(null)}
                disabled={!!closingId}
                className="px-4 py-2 bg-surface border border-border rounded-lg text-foreground hover:bg-surface-elevated transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSingleClose}
                disabled={!!closingId}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-danger hover:opacity-90 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {closingId === rowId(positionToCloseSingle) ? (
                  <>
                    <FiRefreshCw className="h-4 w-4 animate-spin" />
                    <span>Closing...</span>
                  </>
                ) : (
                  'Confirm close'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card overflow-x-auto min-w-0">
        {viewMode === 'masterFollowers' && !masterId ? (
          <div className="text-center py-12 text-muted">Select a master to load followers’ open positions.</div>
        ) : viewMode === 'allOanda' && brokerFilter === 'none' ? (
          <div className="text-center py-12 text-muted">Select a broker to load open positions.</div>
        ) : positions.length === 0 ? (
          <div className="text-center py-12 text-muted">
            {viewMode === 'allOanda' && brokerFilter !== 'all'
              ? `No ${
                  brokerFilter === 'oanda' ? 'OANDA' : brokerFilter === 'genesis' ? 'GenesisFX' : 'Webull'
                } open positions found.`
              : pagination.total === 0
              ? viewMode === 'allOanda'
                ? 'No open positions (OANDA, SnapTrade/Webull, or GenesisFX).'
                : 'No open positions for this master’s followers.'
              : 'No positions match your search.'}
          </div>
        ) : (
          <>
            <div className="table-scroll">
            <table className={`w-full ${showBrokerCols ? 'min-w-[960px]' : 'min-w-[800px]'}`}>
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 w-12">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border accent-primary cursor-pointer align-middle disabled:cursor-not-allowed disabled:opacity-40"
                      checked={allPageRowsSelected}
                      disabled={selectableOnPage.length === 0}
                      onChange={toggleSelectAll}
                      title="Select all closeable positions on this page"
                      aria-label="Select all positions on this page"
                    />
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">User</th>
                  {showBrokerCols && (
                    <>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Broker</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Type</th>
                    </>
                  )}
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Symbol</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Side</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Quantity</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Avg Entry</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Market Value</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Unrealized P&L</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Unrealized P&L %</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => {
                  const id = rowId(p);
                  const isClosing = closingId === id;
                  const qtyDisplay = p.qty < 0 ? Math.abs(p.qty) : p.qty;
                  const brokerLabel = getBrokerLabel(p);
                  return (
                    <tr key={id} className="border-b border-border hover:bg-surface-elevated transition-colors">
                      <td className="py-3 px-4">
                        {isBulkSelectable(p) ? (
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border accent-primary cursor-pointer align-middle"
                            checked={selectedIds.has(id)}
                            onChange={() => toggleSelect(id)}
                            aria-label={`Select ${p.symbol} for ${p.userName}`}
                          />
                        ) : (
                          <span className="text-xs text-muted pl-1">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-foreground">{p.userName}</p>
                          <p className="text-xs text-muted">{p.userEmail}</p>
                        </div>
                      </td>
                      {showBrokerCols && (
                        <>
                          <td className="py-3 px-4 text-sm text-foreground">{brokerLabel}</td>
                          <td className="py-3 px-4 text-xs text-muted capitalize">{p.productType || '—'}</td>
                        </>
                      )}
                      <td className="py-3 px-4 font-medium text-foreground">{p.symbol}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${p.side === 'long' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}
                        >
                          {p.side}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-foreground">{qtyDisplay.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-foreground">
                        {p.avgEntryPrice != null ? Number(p.avgEntryPrice).toFixed(5) : '–'}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground">
                        {p.marketValue != null ? `$${Number(p.marketValue).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '–'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-semibold ${(p.unrealizedPl ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                          {p.unrealizedPl != null ? `$${Number(p.unrealizedPl).toFixed(2)}` : '–'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span
                          className={`font-semibold ${(p.unrealizedPlPercent ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}
                        >
                          {p.unrealizedPlPercent != null ? `${Number(p.unrealizedPlPercent).toFixed(2)}%` : '–'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {isCloseable(p) ? (
                          <button
                            onClick={() => setPositionToCloseSingle(p)}
                            disabled={isBulkClosing || isClosing}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-danger hover:bg-danger/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Close position"
                          >
                            <FiXCircle className="h-4 w-4" />
                            {isClosing ? 'Closing...' : 'Close'}
                          </button>
                        ) : (
                          <span className="text-xs text-muted">View only</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {pagination.pages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-4 pt-4 border-t border-border">
                <div className="text-sm text-muted text-center sm:text-left">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((pg) => Math.max(1, pg - 1))}
                    disabled={pagination.page <= 1}
                    className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FiChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="px-4 py-2 text-sm font-medium text-foreground">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((pg) => Math.min(pagination.pages, pg + 1))}
                    disabled={pagination.page >= pagination.pages}
                    className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FiChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminOpenPositions;